import { featureFlags } from "@/config/env";
import http from "@/services/http";
import { mockManager } from "@/services/mock";
import type { BrandSearchItem, BrandSearchResponse } from "@/types/api";
import { getSourceId } from "../../utils/constants";

export interface SearchRow {
  brandId?: string;
  brand: string;
  industryId?: string; // 当前行业ID
  currentCategoryName?: string; // 当前展示的行业名称
  industryIds?: string[]; // 所有行业ID数组
  tags: string[]; // 所有行业名称
}

export interface SearchResponse {
  items: SearchRow[];
  rawItems: BrandSearchItem[]; // 新增：原始API数据
}

export const searchApi = {
  async search(keyword: string, signal?: AbortSignal): Promise<SearchResponse> {
    // 使用统一的 Mock 数据管理器
    if (featureFlags.enableMock) {
      const mockData = await mockManager.searchBrands(keyword);
      console.log("🔍 Mock搜索原始数据:", mockData);

      // 完全展开品牌+行业组合，每行只显示当前行业
      const items: SearchRow[] = mockData.map((item) => {
        const allIndustryIds = mockData
          .filter((x) => x.brandId === item.brandId)
          .map((x) => x.categoryId);
        const allIndustryNames = mockData
          .filter((x) => x.brandId === item.brandId)
          .map((x) => x.categoryName);

        const result = {
          brandId: item.brandId,
          brand: item.brandName,
          industryId: item.categoryId, // 当前行业ID
          currentCategoryName: item.categoryName, // 当前展示的行业
          industryIds: allIndustryIds, // 所有行业ID
          tags: allIndustryNames, // 所有行业名称
        };

        console.log(
          `🏷️ 转换结果 - ${item.brandName}+${item.categoryName}:`,
          result
        );
        return result;
      });

      console.log("📊 最终搜索结果:", items);
      const mock: SearchResponse = {
        items,
        rawItems: mockData,
      };
      return mock;
    }

    try {
      const response = await http.get<BrandSearchResponse>("brands/search", {
        params: { keyword, sourceId: getSourceId() },
        signal,
      });

      console.log("Raw API response:", response);

      // 检查响应结构
      if (!response || !response.data) {
        console.error("No response data:", response);
        return { items: [], rawItems: [] };
      }

      const data = response.data;
      console.log("Response data:", data);

      // 检查数据结构 - 支持不同的响应格式
      let apiData: BrandSearchItem[] = [];

      if (Array.isArray(data)) {
        // 如果 data 直接是数组
        apiData = data;
      } else if (data.data && Array.isArray(data.data)) {
        // 如果 data.data 是数组
        apiData = data.data;
      } else if ((data as any).items && Array.isArray((data as any).items)) {
        // 如果 data.items 是数组
        apiData = (data as any).items;
      } else {
        console.error("Invalid API response structure:", data);
        return { items: [], rawItems: [] };
      }

      console.log("Parsed API data:", apiData);

      // 应用搜索匹配逻辑
      const q = (keyword || "").trim().toLowerCase();
      let matched: BrandSearchItem[] = [];

      if (q) {
        // 搜索匹配的品牌和分类
        matched = apiData.filter(
          (item) =>
            item.brandName &&
            item.categoryName &&
            (item.brandName.toLowerCase().includes(q) ||
              item.categoryName.toLowerCase().includes(q))
        );
      } else {
        // 如果没有关键词，返回前5个作为推荐
        matched = apiData.slice(0, 5);
      }

      // 完全展开品牌+行业组合，每行只显示当前行业
      const items: SearchRow[] = matched.map((item) => ({
        brandId: item.brandId,
        brand: item.brandName,
        industryId: item.categoryId, // 当前行业ID
        currentCategoryName: item.categoryName, // 当前展示的行业
        industryIds: apiData // 所有行业ID
          .filter((x) => x.brandId === item.brandId)
          .map((x) => x.categoryId),
        tags: apiData // 所有行业名称
          .filter((x) => x.brandId === item.brandId)
          .map((x) => x.categoryName),
      }));

      console.log("API search result:", {
        keyword: q,
        matched: items.length,
        items,
      });
      return {
        items,
        rawItems: matched, // 新增：原始数据
      };
    } catch (error) {
      // 错误处理 - 根据API文档的错误响应格式
      console.error("Search API error:", error);

      // 使用专门的错误处理器处理品牌搜索错误
      //const errorMessage = ApiErrorHandler.handleBrandSearchError(error)
      //console.error('Brand search error:', errorMessage)

      // 如果是API业务错误，错误信息已经在HTTP拦截器中显示
      // 这里返回空结果，避免重复显示错误信息
      return {
        items: [],
        rawItems: [], // 新增：原始数据
      };
    }
  },
};

export default searchApi;
